<template>
  <div class="careers-page">
    <!-- HERO -->
    <header class="cr-hero">
      <nav class="cr-nav">
        <a
          href="/home"
          class="cr-logo"
        ><img
          src="/logo.png"
          alt="TUT'TOP"
        ></a>
        <div class="cr-nav-links">
          <a href="/home">{{ $t('nav.home') }}</a>
          <a href="/contact">{{ $t('nav.contact') }}</a>
        </div>
      </nav>
      <div class="cr-hero-content">
        <h1>{{ $t('careers.heroTitle') }} <span>TUT'TOP</span></h1>
        <p>{{ $t('careers.heroSubtitle') }}</p>
      </div>
      <div class="cr-hero-shapes">
        <div class="cr-shape cr-shape-1" />
        <div class="cr-shape cr-shape-2" />
        <div class="cr-shape cr-shape-3" />
      </div>
    </header>

    <!-- FILTERS -->
    <section class="cr-section">
      <div class="cr-filters">
        <div class="cr-search-wrap">
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#9ca3af"
            stroke-width="2"
          ><circle
            cx="11"
            cy="11"
            r="8"
          /><line
            x1="21"
            y1="21"
            x2="16.65"
            y2="16.65"
          /></svg>
          <input
            v-model="search"
            :placeholder="$t('careers.searchPlaceholder')"
            class="cr-search"
          >
        </div>
        <div class="cr-filter-tags">
          <button
            v-for="t in types"
            :key="t.value"
            class="cr-tag"
            :class="{ 'cr-tag-active': activeType === t.value }"
            @click="activeType = activeType === t.value ? '' : t.value"
          >{{ t.label }}</button>
        </div>
      </div>

      <!-- LOADING -->
      <div
        v-if="loading"
        class="cr-loading"
      >
        <div class="cr-spinner" />
      </div>

      <!-- EMPTY -->
      <div
        v-else-if="filteredOffers.length === 0"
        class="cr-empty"
      >
        <svg
          width="64"
          height="64"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#38B6FF"
          stroke-width="1.5"
        ><rect
          x="2"
          y="7"
          width="20"
          height="14"
          rx="2"
        /><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" /></svg>
        <h3>{{ $t('careers.noOffers') }}</h3>
        <p>{{ $t('careers.comeBackSoon') }}</p>
      </div>

      <!-- OFFERS GRID -->
      <div
        v-else
        class="cr-grid"
      >
        <router-link
          v-for="o in filteredOffers"
          :key="o._id"
          :to="'/carrieres/' + o._id"
          class="cr-card"
        >
          <div class="cr-card-top">
            <span
              class="cr-type-badge"
              :class="'cr-type-' + o.type"
            >{{ typeLabel(o.type) }}</span>
            <span class="cr-date">{{ timeAgo(o.createdAt) }}</span>
          </div>
          <h3 class="cr-card-title">
            {{ o.title }}
          </h3>
          <p class="cr-card-dept">
            {{ o.department }}
          </p>
          <div class="cr-card-meta">
            <span class="cr-meta-item">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              ><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle
                cx="12"
                cy="10"
                r="3"
              /></svg>
              {{ o.location }}
            </span>
            <span class="cr-meta-item">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              ><circle
                cx="12"
                cy="12"
                r="10"
              /><polyline points="12 6 12 12 16 14" /></svg>
              {{ o.duration }}
            </span>
          </div>
          <div class="cr-card-cta">
            {{ $t('careers.viewOffer') }}
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
            ><line
              x1="5"
              y1="12"
              x2="19"
              y2="12"
            /><polyline points="12 5 19 12 12 19" /></svg>
          </div>
        </router-link>
      </div>
    </section>

    <!-- FOOTER -->
    <footer class="cr-footer">
      <div class="cr-footer-inner">
        <div class="cr-footer-brand">
          <img
            src="/logo.png"
            alt="TUT'TOP"
          >
          <p>{{ $t('careers.footerTagline') }}</p>
        </div>
        <div class="cr-footer-social">
          <h4>{{ $t('careers.followUs') }}</h4>
          <div class="cr-social-links">
            <a
              href="https://www.linkedin.com/company/tut-top/"
              target="_blank"
              rel="noopener"
              title="LinkedIn"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
              ><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>
            </a>
            <a
              href="https://www.instagram.com/its.tuttop/"
              target="_blank"
              rel="noopener"
              title="Instagram"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
              ><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" /></svg>
            </a>
          </div>
        </div>
        <div class="cr-footer-contact">
          <h4>{{ $t('nav.contact') }}</h4>
          <a href="mailto:contact@tuttop.io">contact@tuttop.io</a>
        </div>
      </div>
      <div class="cr-footer-bottom">
        <p>{{ $t('footer.rights') }}</p>
      </div>
    </footer>
  </div>
</template>

<script>
import axios from '@/libs/axios'

export default {
  data() {
    return {
      offers: [],
      loading: true,
      search: '',
      activeType: '',
    }
  },
  computed: {
    types() {
      return [
        { value: 'pfe', label: this.$t('careers.types.pfe') },
        { value: 'stage_2_mois', label: this.$t('careers.types.stage_2_mois') },
        { value: 'stage_4_mois', label: this.$t('careers.types.stage_4_mois') },
        { value: 'stage', label: this.$t('careers.types.stage') },
        { value: 'cdd', label: this.$t('careers.types.cdd') },
        { value: 'cdi', label: this.$t('careers.types.cdi') },
        { value: 'alternance', label: this.$t('careers.types.alternance') },
        { value: 'freelance', label: this.$t('careers.types.freelance') },
      ]
    },
    filteredOffers() {
      let list = this.offers
      if (this.activeType) list = list.filter(o => o.type === this.activeType)
      if (this.search.trim()) {
        const q = this.search.toLowerCase()
        list = list.filter(o => o.title.toLowerCase().includes(q) || o.department.toLowerCase().includes(q) || o.location.toLowerCase().includes(q))
      }
      return list
    },
  },
  mounted() {
    this.fetchOffers()
  },
  methods: {
    async fetchOffers() {
      try {
        const { data } = await axios.get('/internships')
        this.offers = data
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    typeLabel(t) {
      const key = `careers.types.${t}`
      const label = this.$t(key)
      return label === key ? t : label
    },
    timeAgo(d) {
      if (!d) return ''
      const diff = Date.now() - new Date(d).getTime()
      const days = Math.floor(diff / 86400000)
      if (days === 0) return this.$t('careers.today')
      if (days === 1) return this.$t('careers.yesterday')
      if (days < 7) return this.$t('careers.daysAgo', { n: days })
      if (days < 30) return this.$t('careers.weeksAgo', { n: Math.floor(days / 7) })
      const loc = this.$i18n.locale === 'fr' ? 'fr-FR' : 'en-US'
      return new Date(d).toLocaleDateString(loc, { day: 'numeric', month: 'short' })
    },
  },
}
</script>

<style scoped>
* { box-sizing: border-box; margin: 0; padding: 0; }
.careers-page {
  min-height: 100vh;
  background: #f0f6ff;
  font-family: 'Montserrat', 'Inter', sans-serif;
  color: #1a1a2e;
}

/* HERO */
.cr-hero {
  position: relative;
  background: linear-gradient(135deg, #38B6FF, #0884D0);
  padding: 0 0 80px;
  overflow: hidden;
}
.cr-nav {
  display: flex; align-items: center; justify-content: space-between;
  padding: 20px 40px; position: relative; z-index: 2;
}
.cr-nav img { height: 48px; }
.cr-nav-links { display: flex; gap: 24px; }
.cr-nav-links a { color: rgba(255,255,255,0.85); text-decoration: none; font-size: 0.9rem; font-weight: 600; transition: color 0.2s; }
.cr-nav-links a:hover { color: #fff; }
.cr-hero-content {
  text-align: center; padding: 60px 24px 0; position: relative; z-index: 2;
}
.cr-hero-content h1 { font-size: 2.6rem; color: #fff; font-weight: 800; margin-bottom: 16px; line-height: 1.2; }
.cr-hero-content h1 span { color: #FFD600; }
.cr-hero-content p { color: rgba(255,255,255,0.9); font-size: 1.1rem; max-width: 560px; margin: 0 auto; line-height: 1.6; }
.cr-hero-shapes { position: absolute; inset: 0; pointer-events: none; }
.cr-shape {
  position: absolute; border-radius: 50%; opacity: 0.08; background: #fff;
}
.cr-shape-1 { width: 300px; height: 300px; top: -80px; right: -60px; }
.cr-shape-2 { width: 200px; height: 200px; bottom: -40px; left: 10%; }
.cr-shape-3 { width: 120px; height: 120px; top: 40%; left: 60%; }

/* SECTION */
.cr-section { max-width: 1100px; margin: -40px auto 0; padding: 0 24px 60px; position: relative; z-index: 3; }
.cr-filters {
  background: #fff; border-radius: 14px; padding: 20px 24px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.06); margin-bottom: 32px;
  display: flex; align-items: center; gap: 20px; flex-wrap: wrap;
}
.cr-search-wrap {
  display: flex; align-items: center; gap: 10px; flex: 1; min-width: 200px;
  background: #f8fafc; border-radius: 10px; padding: 0 14px;
}
.cr-search {
  border: none; background: transparent; padding: 12px 0; font-size: 0.9rem;
  outline: none; width: 100%; color: #1a1a2e; font-family: inherit;
}
.cr-filter-tags { display: flex; gap: 8px; flex-wrap: wrap; }
.cr-tag {
  padding: 8px 16px; border-radius: 20px; font-size: 0.82rem; font-weight: 600;
  background: #f0f6ff; color: #0884D0; border: 1px solid transparent;
  cursor: pointer; transition: all 0.2s;
}
.cr-tag:hover { background: #e0f0ff; }
.cr-tag-active { background: #38B6FF; color: #fff; }

.cr-loading { text-align: center; padding: 80px; }
.cr-spinner {
  width: 36px; height: 36px; border: 3px solid #e0e7ef; border-top-color: #38B6FF;
  border-radius: 50%; animation: spin 0.7s linear infinite; margin: 0 auto;
}
@keyframes spin { to { transform: rotate(360deg); } }
.cr-empty { text-align: center; padding: 80px 20px; }
.cr-empty svg { margin-bottom: 16px; opacity: 0.5; }
.cr-empty h3 { color: #1a1a2e; margin-bottom: 8px; }
.cr-empty p { color: #64748b; }

/* CARDS GRID */
.cr-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 20px; }
.cr-card {
  background: #fff; border-radius: 14px; padding: 24px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.04); text-decoration: none; color: inherit;
  transition: all 0.25s; border: 1px solid transparent; display: flex; flex-direction: column;
}
.cr-card:hover { transform: translateY(-4px); box-shadow: 0 8px 30px rgba(56,182,255,0.12); border-color: #38B6FF; }
.cr-card-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 14px; }
.cr-type-badge { padding: 4px 12px; border-radius: 20px; font-size: 0.75rem; font-weight: 600; }
.cr-type-pfe { background: #fce4ec; color: #c62828; }
.cr-type-stage_2_mois { background: #e8f6ff; color: #0884D0; }
.cr-type-stage_4_mois { background: #e3f2fd; color: #1565c0; }
.cr-type-stage { background: #e8f6ff; color: #0884D0; }
.cr-type-cdd { background: #fff8e1; color: #f57f17; }
.cr-type-cdi { background: #e8fff0; color: #28c76f; }
.cr-type-alternance { background: #fff3e0; color: #e65100; }
.cr-type-freelance { background: #f3e8ff; color: #7c3aed; }
.cr-date { font-size: 0.78rem; color: #9ca3af; }
.cr-card-title { font-size: 1.1rem; font-weight: 700; margin-bottom: 6px; color: #1a1a2e; }
.cr-card-dept { font-size: 0.85rem; color: #64748b; margin-bottom: 16px; }
.cr-card-meta { display: flex; gap: 16px; margin-bottom: 18px; }
.cr-meta-item {
  display: flex; align-items: center; gap: 5px;
  font-size: 0.82rem; color: #64748b;
}
.cr-card-cta {
  margin-top: auto; display: flex; align-items: center; gap: 6px;
  font-size: 0.85rem; font-weight: 700; color: #38B6FF;
}

/* FOOTER */
.cr-footer { background: #0d1b2a; padding: 48px 24px 0; color: #fff; }
.cr-footer-inner {
  max-width: 1100px; margin: 0 auto;
  display: grid; grid-template-columns: 2fr 1fr 1fr; gap: 40px;
}
.cr-footer-brand img { height: 40px; margin-bottom: 12px; }
.cr-footer-brand p { color: rgba(255,255,255,0.6); font-size: 0.88rem; line-height: 1.5; }
.cr-footer h4 { font-size: 0.9rem; font-weight: 700; margin-bottom: 14px; color: #fff; }
.cr-social-links { display: flex; gap: 12px; }
.cr-social-links a {
  width: 40px; height: 40px; border-radius: 10px; background: rgba(255,255,255,0.08);
  display: flex; align-items: center; justify-content: center; color: #fff;
  transition: all 0.2s; text-decoration: none;
}
.cr-social-links a:hover { background: #38B6FF; transform: translateY(-2px); }
.cr-footer-contact a { color: rgba(255,255,255,0.7); text-decoration: none; font-size: 0.88rem; }
.cr-footer-contact a:hover { color: #38B6FF; }
.cr-footer-bottom {
  text-align: center; padding: 24px 0; margin-top: 40px;
  border-top: 1px solid rgba(255,255,255,0.08);
}
.cr-footer-bottom p { color: rgba(255,255,255,0.4); font-size: 0.8rem; }

@media (max-width: 768px) {
  .cr-hero-content h1 { font-size: 1.8rem; }
  .cr-nav { padding: 10px 16px; }
  .cr-nav img { height: 36px; }
  .cr-nav-links { gap: 14px; }
  .cr-nav-links a { font-size: 0.82rem; }
  .cr-hero { padding: 0 0 50px; }
  .cr-hero-content { padding: 40px 16px 0; }
  .cr-hero-content p { font-size: 0.95rem; }
  .cr-grid { grid-template-columns: 1fr; }
  .cr-footer-inner { grid-template-columns: 1fr; gap: 28px; }
}
</style>
